import { createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Check, ClipboardCopy } from "@fleet/icons";
import { buildDiagnosticsReport, type DiagnosticSection } from "@space/shared";

import { Button } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";

/**
 * Copies a pasteable diagnostics report to the clipboard.
 *
 * The component only COLLECTS data and delegates formatting to
 * `buildDiagnosticsReport` (shared, pure and tested function). If the report were
 * assembled here, each app would have its own version and they'd diverge as soon as
 * someone added a field.
 *
 * Receives the SAME sections array the screen renders. It used to receive its
 * own object while the screen painted its rows independently: two
 * sources for the same thing, so adding a row up top left the report
 * incomplete without anyone noticing.
 *
 * Labels: uses `Copy` and `Copied!`, which are already translated into the eleven
 * locales. A new string like "Copy diagnostics" would show up in ENGLISH in all of
 * them without the i18n gate complaining, because it only validates what's already
 * in the catalog.
 */
/** How long "Copied!" is kept before reverting to the normal label. */
const CONFIRMATION_MS = 2000;

export function CopyDiagnosticsButton(props: {
  appName: string;
  /** The SAME model the screen renders. */
  sections?: () => DiagnosticSection[];
  secciones?: () => DiagnosticSection[];
}) {
  const { t } = useI18n();
  const [copied, setCopied] = createSignal(false);
  const [error, setError] = createSignal(false);
  const getSections = () => props.sections?.() ?? props.secciones?.() ?? [];

  const copy = async () => {
    const text = buildDiagnosticsReport({
      appName: props.appName,
      timestamp: new Date().toISOString(),
      sections: getSections(),
    });
    try {
      await navigator.clipboard.writeText(text);
      setError(false);
      setCopied(true);
      setTimeout(() => setCopied(false), CONFIRMATION_MS);
    } catch {
      // The clipboard fails without permission or outside a secure context. Silencing it
      // would leave the user thinking they copied it: the failure is flagged and the
      // report is left selectable as a plan B.
      setError(true);
    }
  };

  return (
    <div class="space-y-2">
      <Button
        variant="outline"
        class="w-full gap-2"
        data-testid="copy-diagnostics"
        onClick={() => void copy()}
      >
        <Show when={copied()} fallback={<ClipboardCopy size={18} />}>
          <Check size={18} class="text-success" />
        </Show>
        {copied() ? t(msg`Copied!`) : t(msg`Copy`)}
      </Button>

      {/* Plan B for when the clipboard isn't available: the report is visible
          to select by hand. Without this, the button would be a dead end
          exactly in the environments where debugging is needed most. */}
      <Show when={error()}>
        <textarea
          data-testid="diagnostics-fallback"
          class="h-40 w-full rounded-md border border-border bg-muted/30 p-2 font-mono text-xs"
          readonly
          onFocus={(e) => e.currentTarget.select()}
        >
          {buildDiagnosticsReport({
            appName: props.appName,
            timestamp: new Date().toISOString(),
            sections: getSections(),
          })}
        </textarea>
      </Show>
    </div>
  );
}
